'use client';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import { logout } from '@/utils/auth';
import { useToast } from '@/hooks/useToast';
import { usePermissions, buildCanHelpers } from '@/hooks/usePermissions';

// Icons + labels for every menu key the strip can show (matches Sidebar.jsx).
const MENU = {
  dashboard: { icon: '/images/img_layout_dashboard.svg', label: 'Dashboard' },
  messages: { icon: '/images/message.svg', label: 'Messages' },
  helpdesk: { icon: '/images/help.svg', label: 'Help Desk' },
  students: { icon: '/images/img_school.svg', label: 'Students' },
  staff: { icon: '/images/img_user_shield.svg', label: 'Staff' },
  teachers: { icon: '/images/img_users.svg', label: 'Teachers' },
  classes: { icon: '/images/img_school_bell.svg', label: 'Classes' },
  'class-list': { icon: '/images/img_school_bell.svg', label: 'Class List' },
  attendance: { icon: '/images/img_calendar_share.svg', label: 'Attendance' },
  homework: { icon: '/images/img_school_bell.svg', label: 'Home Work' },
  assignments: { icon: '/images/img_book.svg', label: 'Assignments' },
  'class-test': { icon: '/images/img_hexagonal_prism_plus.svg', label: 'Class Test' },
  'consent-form': { icon: '/images/img_clipboard_data.svg', label: 'Consent' },
  subject: { icon: '/images/img_book.svg', label: 'Subject' },
  timetable: { icon: '/images/img_table.svg', label: 'Time Table' },
  examinations: { icon: '/images/img_hexagonal_prism_plus.svg', label: 'Examinations' },
  'grade-list': { icon: '/images/img_hexagonal_prism_plus.svg', label: 'Grade List' },
  'exam-list': { icon: '/images/img_hexagonal_prism_plus.svg', label: 'Exam List' },
  'exam-schedule': { icon: '/images/img_hexagonal_prism_plus.svg', label: 'Exam Schedule' },
  'exam-results': { icon: '/images/img_chart_infographic.svg', label: 'Exam Results' },
  promote: { icon: '/images/img_lifebuoy.svg', label: 'Promote' },
  fees: { icon: '/images/img_report_money.svg', label: 'Fees' },
  library: { icon: '/images/img_notebook.svg', label: 'Library' },
  sports: { icon: '/images/img_run.svg', label: 'Sports' },
  hostel: { icon: '/images/img_building_fortress.svg', label: 'Hostel' },
  transport: { icon: '/images/img_bus.svg', label: 'Transport' },
  pickup: { icon: '/images/img_exchange.svg', label: 'Student Pickup' },
  staffs: { icon: '/images/img_users_group.svg', label: 'Staffs' },
  departments: { icon: '/images/img_layout_distribute_horizontal.svg', label: 'Departments' },
  'hrm-attendance': { icon: '/images/img_calendar_share.svg', label: 'Attendance' },
  'hrm-leaves': { icon: '/images/img_calendar_stats.svg', label: 'Leaves' },
  holidays: { icon: '/images/img_briefcase.svg', label: 'Holidays' },
  payroll: { icon: '/images/img_moneybag.svg', label: 'Payroll' },
  notice: { icon: '/images/img_clipboard_data.svg', label: 'Notice' },
  events: { icon: '/images/img_calendar_question.svg', label: 'Events' },
  'attendance-report': { icon: '/images/img_calendar_due.svg', label: 'Attendance Report' },
  'class-report': { icon: '/images/img_graph.svg', label: 'Class Report' },
  'student-report': { icon: '/images/img_chart_infographic.svg', label: 'Student Report' },
  'leave-report': { icon: '/images/img_line.svg', label: 'Leave Report' },
  'fees-report': { icon: '/images/img_mask.svg', label: 'Fees Report' },
  profile: { icon: '/images/img_layout_dashboard.svg', label: 'Profile' },
  settings: { icon: '/images/settings-up.svg', label: 'Settings' },
  'transfer-certificate': { icon: '/images/img_file_text.svg', label: 'Transfer Certificate' },
  'manage-users': { icon: '/images/img_users.svg', label: 'Users' },
  schools: { icon: '/images/img_school.svg', label: 'Schools' },
  logout: { icon: '/images/logout.svg', label: 'LogOut' },
};

const item = (key, href, extra = {}) => ({ key, href, ...MENU[key], ...extra });

// The strip per role. `section` on an item means "only when the signed-in
// user can READ that section" — the same gate the sidebar applies — so a
// staff member who is not the school admin never gets Students / Fees /
// Settings icons that bounce them back to their dashboard when tapped
// (Bug Report 8, BUG-048). Items without a section are role-wide.
const ROLE_MENUS = {
  superadmin: [
    item('dashboard', '/superadmin-dashboard'),
    item('students', '/students'),
    item('staff', '/staff'),
    item('teachers', '/teachers'),
    item('classes', '/classes'),
    item('subject', '/subject'),
    item('timetable', '/timetable'),
    item('examinations', '/examinations', { label: 'Exams' }),
    item('promote', '/promote'),
    item('fees', '/fees'),
    item('library', '/library'),
    item('sports', '/sports'),
    item('hostel', '/hostel'),
    item('transport', '/transport'),
    item('notice', '/notice'),
    item('events', '/events'),
    item('manage-users', '/superadmin/manage-users'),
    item('schools', '/superadmin/schools'),
    item('settings', '/settings'),
    item('logout', null, { isLogout: true }),
  ],
  schooladmin: [
    item('dashboard', '/admin-dashboard'),
    item('messages', '/messages'),
    item('helpdesk', '/helpdesk', { section: 'helpdesk' }),
    item('students', '/students', { section: 'students' }),
    item('staff', '/staff', { section: 'staff' }),
    item('teachers', '/teachers', { section: 'teachers' }),
    item('timetable', '/timetable', { section: 'timetable' }),
    item('fees', '/fees', { section: 'fees' }),
    item('notice', '/notice', { section: 'notice' }),
    item('events', '/events', { section: 'events' }),
    item('settings', '/settings', { section: 'settings' }),
  ],
  teacher: [
    item('dashboard', '/teacher-dashboard'),
    item('messages', '/messages'),
    item('attendance', '/classes/attendance'),
    item('homework', '/classes/homework'),
    item('assignments', '/classes/assignments'),
    item('class-test', '/classes/class-test'),
    item('consent-form', '/classes/consent-form'),
    item('exam-results', '/examinations/results', { label: 'Exam Result' }),
    item('notice', '/notice'),
    item('events', '/events'),
  ],
  student: [
    item('dashboard', '/student-dashboard'),
    item('messages', '/messages'),
    item('attendance', '/classes/attendance'),
    item('homework', '/classes/homework'),
    item('timetable', '/timetable'),
    item('assignments', '/classes/assignments'),
    item('class-test', '/classes/class-test'),
    item('consent-form', '/classes/consent-form'),
    item('notice', '/notice'),
    item('events', '/events'),
  ],
};

// Any OTHER staff role (a transport manager, a librarian, an office clerk)
// used to fall through to the schooladmin strip. They get the modules their
// grants actually open, nothing else.
const STAFF_MENU = [
  item('dashboard', '/staff-dashboard'),
  item('messages', '/messages'),
  item('transport', '/transport', { section: 'transport' }),
  item('pickup', '/pickup', { section: 'pickup' }),
  item('library', '/library', { section: 'library' }),
  item('sports', '/sports', { section: 'sports' }),
  item('hostel', '/hostel', { section: 'hostel' }),
  item('students', '/students', { section: 'students' }),
  item('staff', '/staff', { section: 'staff' }),
  item('teachers', '/teachers', { section: 'teachers' }),
  item('timetable', '/timetable', { section: 'timetable' }),
  item('fees', '/fees', { section: 'fees' }),
  item('helpdesk', '/helpdesk', { section: 'helpdesk' }),
  item('notice', '/notice', { section: 'notice' }),
  item('events', '/events', { section: 'events' }),
  item('hrm-leaves', '/leaves', { section: 'leaves' }),
  item('payroll', '/payroll', { section: 'payroll' }),
  item('settings', '/settings', { section: 'settings' }),
];

// Map routes to their menu keys (for the "current page" chip).
const routeToMenuKey = {
  '/examinations/schedule': 'exam-schedule',
  '/examinations/grades': 'grade-list',
  '/examinations/list': 'exam-list',
  '/examinations/results': 'exam-results',
  '/examinations': 'examinations',
  '/classes/list': 'class-list',
  '/classes': 'classes',
  '/subject': 'subject',
  '/library': 'library',
  '/sports': 'sports',
  '/hostel': 'hostel',
  '/transport': 'transport',
  '/pickup': 'pickup',
  '/departments': 'departments',
  '/attendance': 'hrm-attendance',
  '/leaves': 'hrm-leaves',
  '/holidays': 'holidays',
  '/payroll': 'payroll',
  '/attendance-report': 'attendance-report',
  '/class-report': 'class-report',
  '/student-report': 'student-report',
  '/leave-report': 'leave-report',
  '/fees-report': 'fees-report',
  '/promote': 'promote',
  '/profile': 'profile',
  '/students': 'students',
  '/staff': 'staff',
  '/teachers': 'teachers',
  '/fees': 'fees',
  '/helpdesk': 'helpdesk',
  '/transfer-certificates': 'transfer-certificate',
};

const getMenuKeyForPath = (path) => {
  if (routeToMenuKey[path]) return routeToMenuKey[path];
  for (const [route, key] of Object.entries(routeToMenuKey)) {
    if (path.startsWith(route + '/')) return key;
  }
  return null;
};

const MobileBottomNav = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { showToast } = useToast();
  const [userRole, setUserRole] = useState(null);
  const permissions = usePermissions();
  const can = useMemo(() => buildCanHelpers(permissions), [permissions]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const role = sessionStorage.getItem('userRole');
      setUserRole(role);
    }
  }, []);

  // The strip for this person. Section-tagged items are kept only when
  // their grants open that section — the sidebar's rule. Role-wide items
  // (dashboard, messages…) always stay.
  const mostUsedMenus = useMemo(() => {
    const base = ROLE_MENUS[userRole] || STAFF_MENU;
    if (userRole === 'superadmin') return base;
    return base.filter((m) => !m.section || can.read(m.section));
  }, [userRole, can]);

  const handleNavigation = (item) => {
    if (item.isLogout) {
      logout(router, showToast);
    } else if (item.href) {
      if (pathname === item.href) {
        // Add reset param to trigger state reset in page
        router.push(`${item.href}?reset=${Date.now()}`);
      } else {
        router.push(item.href);
        // Explicitly reset scroll for mobile navigation
        if (typeof window !== 'undefined') {
          window.scrollTo(0, 0);
          const mainContent = document.querySelector('main');
          if (mainContent) {
            mainContent.scrollTop = 0;
          }
        }
      }
    }
  };

  const scrollContainerRef = useRef(null);

  // Dynamically enhance bottom nav with current page icon if not present
  const getEnhancedMenus = () => {
    const currentMenuKey = getMenuKeyForPath(pathname);

    // Check if current page is already in bottom nav by key OR by href
    const isInBottomNav = mostUsedMenus.some((item) => {
      if (item.key === currentMenuKey) return true;
      if (item.href) {
        if (pathname === item.href) return true;
        if (pathname.startsWith(item.href + '/')) return true;
      }
      return false;
    });

    if (!isInBottomNav && currentMenuKey && MENU[currentMenuKey]) {
      // Create a temporary menu item for the current page
      const tempItem = {
        key: currentMenuKey,
        icon: MENU[currentMenuKey].icon,
        label: MENU[currentMenuKey].label,
        href: pathname,
        isTemporary: true, // Mark as temporary so we know it's dynamically added
      };
      return [tempItem, ...mostUsedMenus];
    }

    return mostUsedMenus;
  };

  const enhancedMenus = getEnhancedMenus();

  // Helper function to check if a menu item is active
  const isMenuItemActive = (item) => {
    if (!item.href) return false;

    // Exact match
    if (pathname === item.href) return true;

    // For nested routes, check if current path starts with the menu item's path
    if (pathname.startsWith(item.href + '/')) return true;

    // For temporary items, check if the menu key matches current path
    if (item.isTemporary) {
      const currentKey = getMenuKeyForPath(pathname);
      return currentKey === item.key;
    }

    // Special handling for related routes
    const pathSegments = pathname.split('/').filter(Boolean);
    const hrefSegments = item.href.split('/').filter(Boolean);

    if (hrefSegments.length > 0 && pathSegments.length > 0) {
      if (hrefSegments[0] === pathSegments[0]) {
        if (hrefSegments.length > 1 && pathSegments.length > 1) {
          return hrefSegments[1] === pathSegments[1];
        }
        return hrefSegments.length === 1;
      }
    }

    return false;
  };

  // Auto-scroll active item to the left
  useEffect(() => {
    if (scrollContainerRef.current && enhancedMenus.length > 0) {
      const container = scrollContainerRef.current;
      const baseActiveIndex = enhancedMenus.findIndex((item) => isMenuItemActive(item));

      if (baseActiveIndex !== -1) {
        const buttons = container.querySelectorAll('button');
        const targetButton = buttons[baseActiveIndex];

        if (targetButton) {
          container.scrollTo({
            left: targetButton.offsetLeft - 16,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [pathname, enhancedMenus]);

  if (!userRole) return null;

  return (
    <nav
      aria-label="Main"
      className="sm:hidden fixed bottom-0 left-0 right-0 z-[100] bg-white/90 backdrop-blur-xl border-t border-gray-100 pb-[env(safe-area-inset-bottom)]"
    >
      <div
        ref={scrollContainerRef}
        className="flex items-center overflow-x-auto no-scrollbar scroll-smooth px-4 h-16"
      >
        <div className="flex items-center gap-1 min-w-max">
          {enhancedMenus.map((item, index) => {
            const isActive = isMenuItemActive(item);

            return (
              <button
                key={`${item.key}-${index}`}
                data-key={item.key}
                onClick={() => handleNavigation(item)}
                aria-label={item.label}
                aria-current={isActive ? 'page' : undefined}
                className={twMerge(
                  'relative flex items-center gap-2.5 px-4 py-2.5 rounded-2xl transition-all duration-300 outline-none select-none shrink-0',
                  isActive
                    ? 'bg-[#0f65cd] text-white shadow-lg shadow-blue-500/20'
                    : 'text-gray-500 hover:bg-gray-100/50 active:scale-95',
                )}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {/* Icon Wrapper */}
                <div
                  className={twMerge(
                    'w-5 h-5 transition-transform duration-300',
                    isActive ? 'scale-110' : 'opacity-70 group-active:scale-90',
                  )}
                >
                  <div
                    className="w-full h-full"
                    style={{
                      WebkitMaskImage: `url(${item.icon})`,
                      maskImage: `url(${item.icon})`,
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      maskPosition: 'center',
                      WebkitMaskSize: 'contain',
                      maskSize: 'contain',
                      backgroundColor: 'currentColor',
                    }}
                  />
                </div>

                {/* Label */}
                <span
                  className={twMerge(
                    'text-xs font-black tracking-tight whitespace-nowrap overflow-hidden transition-all duration-300',
                    isActive ? 'w-auto max-w-[120px] opacity-100' : 'w-0 max-w-0 opacity-0 hidden',
                  )}
                >
                  {item.label}
                </span>

                {/* Active Indicator Dot */}
                {isActive && (
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-white rounded-full opacity-50" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <style jsx global>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </nav>
  );
};

export default MobileBottomNav;
